import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverStateV2 } from './useDriverStateV2';
import type { DriverSortBy, ListParams } from '@relay-towing/api';
import { getList } from '../api/list';

export interface CompanyDriver {
  id: string;
  name: string;
  truckNumber: string | null;
  locationLatitude: number;
  locationLongitude: number;
  status: 'ACTIVE' | 'CLEAR' | 'DISPATCHED' | 'ON_LEAVE';
  email: string;
  phone: string | null;
}

interface UseCompanyDriversOptions {
  mapVisible?: boolean;
  activeTab?: string;
}

const getCompanyDrivers = async (
  token: string,
  companyId: string
): Promise<CompanyDriver[]> => {
  try {
    // Whoever drives: a visible Driver row, any office role.
    const params = {
      pageSize: 1000,
    } satisfies ListParams<DriverSortBy>;
    const { data } = await getList<CompanyDriver>('/drivers', params, {
      token,
      companyId,
    });
    return data.map((driver) => ({
      id: driver.id,
      name: driver.name,
      truckNumber: driver.truckNumber,
      locationLatitude: driver.locationLatitude || 0,
      locationLongitude: driver.locationLongitude || 0,
      status: driver.status,
      email: driver.email,
      phone: driver.phone,
    }));
  } catch (error: any) {
    console.error('Failed to get company drivers:', error);
    throw new Error(error.message || 'Failed to get company drivers');
  }
};

export const useCompanyDrivers = (options?: UseCompanyDriversOptions) => {
  const { mapVisible = false, activeTab = 'calls' } = options || {};
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();
  const companyId = driver?.companyId || null;

  // Determine refetch behavior based on active tab and map visibility
  const isHomeTab = activeTab === 'home';
  const isDispatchTabWithMapVisible = activeTab === 'drivers' && mapVisible;

  // Simple strategy: Always keep data fresh
  // - If map is visible: aggressive refetch (30s interval)
  // - Otherwise: minimal refetch (60s or disabled)
  let staleTime: number;
  let refetchInterval: number | false;

  if (isDispatchTabWithMapVisible) {
    // Map is visible: keep data very fresh
    staleTime = 30 * 1000; // 30 seconds
    refetchInterval = 30 * 1000; // Polling every 30s
  } else if (isHomeTab) {
    // Home tab: moderate refresh
    staleTime = 30 * 1000; // 30 seconds
    refetchInterval = 60 * 1000; // Polling every 60s
  } else {
    // Other cases: minimal refetch to conserve API
    staleTime = 30 * 1000; // 30 seconds
    refetchInterval = false;
  }

  console.log('🚗 [useCompanyDrivers] Config:', {
    activeTab,
    mapVisible,
    staleTime: `${staleTime / 1000}s`,
    refetchInterval: refetchInterval
      ? `${refetchInterval / 1000}s`
      : 'disabled',
    hookEnabled: !!driver,
    driverExists: !!driver ? `yes (${driver.id})` : 'NO - hook will not run',
  });

  const previousDataRef = React.useRef<CompanyDriver[] | null>(null);
  const fetchCountRef = React.useRef(0);

  return useQuery<CompanyDriver[]>({
    queryFn: async () => {
      const fetchNum = ++fetchCountRef.current;
      console.error(
        `🚗🚗🚗 FETCH START #${fetchNum} - THIS IS AN ERROR LOG TO FORCE VISIBILITY 🚗🚗🚗`
      );

      try {
        const token = await getAccessTokenSilently();
        if (!companyId) {
          throw new Error('Company context required for company drivers list');
        }
        const startTime = Date.now();
        const newData = await getCompanyDrivers(token, companyId);
        const duration = Date.now() - startTime;
        console.error(
          `🚗🚗🚗 FETCH #${fetchNum} SUCCESS: ${newData.length} drivers in ${duration}ms 🚗🚗🚗`
        );

        // Compare with previous data to detect changes
        if (previousDataRef.current) {
          const hasChanges =
            newData.length !== previousDataRef.current.length ||
            newData.some((driver, index) => {
              const prevDriver = previousDataRef.current![index];
              return (
                driver.id !== prevDriver.id ||
                driver.locationLatitude !== prevDriver.locationLatitude ||
                driver.locationLongitude !== prevDriver.locationLongitude ||
                driver.status !== prevDriver.status
              );
            });

          if (hasChanges) {
            console.log(
              '🚗 [useCompanyDrivers] NEW DRIVER LOCATION DATA DETECTED'
            );
            newData.forEach((driver, index) => {
              const prevDriver = previousDataRef.current![index];
              if (
                driver.locationLatitude !== prevDriver.locationLatitude ||
                driver.locationLongitude !== prevDriver.locationLongitude ||
                driver.status !== prevDriver.status
              ) {
                console.log(
                  `  📍 ${driver.name}: (${driver.locationLatitude.toFixed(6)}, ${driver.locationLongitude.toFixed(6)}) - ${driver.status}`
                );
              }
            });
          } else {
            console.log(
              '🚗 [useCompanyDrivers] ⏸️ No changes in driver location data'
            );
          }
        } else {
          console.log('🚗 [useCompanyDrivers] Initial driver data loaded:', {
            driverCount: newData.length,
            drivers: newData.map((d) => ({
              name: d.name,
              lat: d.locationLatitude.toFixed(6),
              lng: d.locationLongitude.toFixed(6),
              status: d.status,
            })),
          });
        }

        previousDataRef.current = newData;
        return newData;
      } catch (error) {
        console.error(
          `❌ [useCompanyDrivers] Fetch #${fetchNum} FAILED:`,
          error
        );
        throw error;
      }
    },
    queryKey: ['company-drivers', companyId],
    enabled: !!driver && !!companyId,
    staleTime,
    refetchInterval,
  });
};
